/**
 * "Is this transcript line the learner's?" — asked positively, in one place.
 *
 * Every consumer used to answer by negation: a line belonged to the rep unless it
 * started with the literal `Customer: ` prefix `conversationFromChat` writes. That
 * only holds while the buyer is the ONLY other speaker. As soon as a second persona
 * speaks under its own name, negation hands its lines to the learner, and they get
 * scored, word-counted, highlighted and annotated as rep speech.
 *
 * The label tables below are not semantic keyword matching. They are the exact
 * prefixes this codebase's own transcript writers emit — `conversationFromChat`
 * ("Customer" / the session user's name), the phone bridge ("Agent" / "User") and
 * the on-the-fly runner ("Salesperson" / "You") — i.e. enum values.
 */

/** Prefixes our transcript writers use for the learner. */
export const REP_SPEAKER_LABELS = new Set([
  "you",
  "user",
  "rep",
  "sales rep",
  "salesperson",
  "sales person",
  "seller",
]);

/** Prefixes our transcript writers use for the AI counterpart. */
export const NON_REP_SPEAKER_LABELS = new Set([
  "customer",
  "client",
  "buyer",
  "prospect",
  "persona",
  "agent",
  "assistant",
  "bot",
]);

const MAX_LABEL_WORDS = 5;
const MAX_LABEL_LENGTH = 40;

// A speaker label is a name, not a sentence: it opens with a capital, may carry
// digits ("Agent 47"), an ampersand ("Acme & Co"), parentheses or a dot, and may
// hold lowercase particles in the middle ("Jordan van der Berg"). Requiring the
// LAST word to be capitalised (or numeric) is what keeps ordinary prose such as
// "...confirm that the address is:" from opening a false speaker boundary.
//
// Unicode-aware, not `[A-Za-z]`: personas are named by studios in whatever
// language they sell in, and an ASCII-only rule silently drops "Élodie" and
// "李雷" out of the transcript entirely. `\p{Lo}` is what covers scripts with no
// case distinction at all, where "opens with a capital" has no meaning.
const FIRST_NAME_WORD = /^[\p{Lu}\p{Lo}][\p{L}\p{N}.'’&()/-]*$/u;
const NAME_WORD = /^[\p{Lu}\p{Lo}\p{N}(][\p{L}\p{N}.'’&()/-]*$/u;
const PARTICLE_WORD = /^(?:\p{Ll}[\p{Ll}'’-]{0,4}|&|\+|\/)$/u;

const SPEAKER_LINE = /^\s*([^:\n]{1,80}?)[ \t]*:[ \t]*(.*)$/;

/**
 * Comparison key for a speaker label. Punctuation and case are dropped so
 * "Rucker-Burton" and "rucker burton" are the same person, and so two personas
 * that differ only in case never read as two speakers.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function speakerKey(value) {
  if (typeof value !== "string") return "";
  // Letters and numbers in ANY script survive: `[^a-z0-9]` would reduce "Élodie"
  // to "lodie" and "李雷" to the empty string, at which point the transcript has
  // no way to tell that persona's turns from anyone else's.
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * True when two labels name the same person. A whole-word prefix counts, because
 * one surface may label a turn "Sam" while another has the full "Sam Torres".
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function sameSpeakerName(a, b) {
  const left = speakerKey(a);
  const right = speakerKey(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return left.startsWith(`${right} `) || right.startsWith(`${left} `);
}

/**
 * Split a raw `"Name: text"` line. Deliberately permissive about what precedes
 * the colon — callers that need to reject prose gate on `looksLikeSpeakerName`.
 *
 * @param {unknown} line
 * @returns {{ speaker: string, content: string }|null}
 */
export function splitSpeakerLine(line) {
  if (typeof line !== "string") return null;
  const match = line.match(SPEAKER_LINE);
  if (!match) return null;
  return { speaker: match[1].trim(), content: match[2] };
}

/**
 * The speaker of a raw line or of an already-parsed message.
 *
 * @param {string|{speaker?: string}|null} entry
 * @returns {string|null}
 */
export function speakerOf(entry) {
  if (!entry) return null;
  if (typeof entry === "string") return splitSpeakerLine(entry)?.speaker ?? null;
  if (typeof entry.speaker === "string") return entry.speaker;
  return null;
}

/**
 * Does this label look like somebody's name rather than the middle of a sentence?
 *
 * @param {unknown} label
 * @param {Object} [options]
 * @param {number} [options.minWords]
 * @param {number} [options.maxWords]
 * @param {number} [options.maxLength]
 * @param {Array<string>} [options.knownSpeakers] - Labels accepted whatever their
 *   shape, because the caller already knows who is in the room.
 * @returns {boolean}
 */
export function looksLikeSpeakerName(label, options = {}) {
  const {
    minWords = 1,
    maxWords = MAX_LABEL_WORDS,
    maxLength = 50,
    knownSpeakers = [],
  } = options;

  if (typeof label !== "string") return false;
  const trimmed = label.trim();
  if (!trimmed) return false;

  if (knownSpeakers.some((name) => sameSpeakerName(trimmed, name))) return true;
  if (trimmed.length > maxLength) return false;

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < minWords || words.length > maxWords) return false;
  if (!FIRST_NAME_WORD.test(words[0])) return false;
  if (!NAME_WORD.test(words[words.length - 1])) return false;

  return words
    .slice(1, -1)
    .every((word) => NAME_WORD.test(word) || PARTICLE_WORD.test(word));
}

/**
 * Is this speaker the learner?
 *
 * @param {unknown} speaker
 * @param {Object} [options]
 * @param {string} [options.learnerName] - Whatever the transcript writer used as
 *   the learner's prefix (usually the session user's name).
 * @param {Array<string>} [options.personaNames] - Everyone in the cast. Supplying
 *   this is what stops a second persona being scored as rep speech.
 * @param {boolean} [options.unknownIsRep] - Verdict for a label we cannot place.
 *   Defaults to true, which is exactly what "anything that is not Customer: is the
 *   rep" meant before personas had names.
 * @returns {boolean}
 */
export function isRepSpeaker(speaker, options = {}) {
  const { learnerName = "", personaNames = [], unknownIsRep = true } = options;

  const key = speakerKey(speaker);
  if (!key) return unknownIsRep;

  // Exact matches first, on BOTH sides. `sameSpeakerName` counts a whole-word
  // prefix as the same person, so with persona "Sam" and learner "Sam Lee" a
  // persona-first prefix test would classify every "Sam Lee:" turn as the buyer
  // and drop the learner's own speech out of scoring and annotation.
  if (personaNames.some((name) => speakerKey(name) === key)) return false;
  if (learnerName && speakerKey(learnerName) === key) return true;

  // Only ambiguous prefix matches left. One side matching is an answer; both
  // sides matching is not, so that case falls through to the label tables rather
  // than forcing the persona verdict.
  const prefixPersona = personaNames.some((name) => sameSpeakerName(key, name));
  const prefixLearner = Boolean(learnerName) && sameSpeakerName(key, learnerName);
  if (prefixPersona && !prefixLearner) return false;
  if (prefixLearner && !prefixPersona) return true;

  if (NON_REP_SPEAKER_LABELS.has(key)) return false;
  if (REP_SPEAKER_LABELS.has(key)) return true;

  return unknownIsRep;
}

/**
 * Is this line the learner's? Accepts a raw `"Name: text"` line or a parsed
 * `{ speaker, message|content }` object. An unlabelled continuation line inherits
 * the caller's default, which keeps line-filtering consumers behaving as before.
 *
 * @param {string|{speaker?: string}|null} entry
 * @param {Object} [options] - Same options as `isRepSpeaker`.
 * @returns {boolean}
 */
export function isRepLine(entry, options = {}) {
  const speaker = speakerOf(entry);
  if (speaker === null) return options.unknownIsRep ?? true;
  return isRepSpeaker(speaker, options);
}

/**
 * Who spoke in this transcript, and did more than one persona take part?
 *
 * The UI needs this to decide between today's fixed "Customer"/"You" labels and
 * rendering real speaker names: a session is multi-speaker only when two distinct
 * non-rep speakers appear, so every single-persona transcript keeps its labels.
 *
 * @param {Array<string|{speaker?: string}>} messages
 * @param {Object} [options] - Same options as `isRepSpeaker`.
 * @returns {{ speakers: Array<{speaker: string, isRep: boolean}>, personaSpeakers: Array<string>, isMultiSpeaker: boolean }}
 */
export function classifyTranscriptSpeakers(messages, options = {}) {
  const speakers = [];
  const seen = new Set();

  for (const message of Array.isArray(messages) ? messages : []) {
    const speaker = speakerOf(message);
    const key = speakerKey(speaker);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    speakers.push({ speaker, isRep: isRepSpeaker(speaker, options) });
  }

  const personaSpeakers = speakers
    .filter((entry) => !entry.isRep)
    .map((entry) => entry.speaker);

  return { speakers, personaSpeakers, isMultiSpeaker: personaSpeakers.length > 1 };
}

/**
 * Normalize a persona's name into a label that is safe to write as a transcript
 * prefix: a colon inside a name would open a false speaker boundary, and a
 * newline would split the turn in half. Length and word count are capped so every
 * parser downstream still recognises the label as a name.
 *
 * This is the ONLY place a persona's name is rewritten, and only for the prefix —
 * nothing else renames a persona.
 *
 * @param {unknown} name
 * @returns {string} Empty when there is nothing usable, so callers can fall back.
 */
export function transcriptSpeakerLabel(name) {
  if (typeof name !== "string") return "";

  const cleaned = name
    .replace(/[:\r\n\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";

  const label = cleaned.split(" ").slice(0, MAX_LABEL_WORDS).join(" ");
  if (label.length <= MAX_LABEL_LENGTH) return label;

  const clipped = label.slice(0, MAX_LABEL_LENGTH);
  return clipped.replace(/\s+\S*$/, "").trim() || clipped.trim();
}
